import * as Sentry from "@sentry/react";
import axios from "axios";
import { isEqual } from "lodash";
import type { Action } from "redux";
import { all, call, delay, fork, put, race, select, take } from "redux-saga/effects";

import { authenticatedAction } from "../../actions/auth";
import { CALL_SEND_PEER_INFO, CALL_SET_VIDEO_PARTICIPANTS } from "../../actions/types";
import { getVideoParticipantIds } from "../../../lib/participants";
import { showMessageBox } from "../modals/actions";
import type { RootState } from "../rootReducer";

import { broadcastParticipantUpdate, initializeCall } from "./actions";

const LAYOUT_UPDATE_DEBOUNCE = 2500;

type VisitorId = string;

const getCallState = (store: RootState) => store.call;

const isLayoutUpdate = ({ payload }) =>
  payload && (Array.isArray(payload) ? payload : [payload]).some((p) => "isVideoRecorded" in p.data);

function* handleRecordingLayoutChanges() {
  let pendingLayoutUpdate = false;
  let updateInProgress = false;

  // null means everyone is recorded (i.e. default mode when individual
  // participants are not selected)
  let lastSuccessfullLayout: VisitorId[];

  function* revertToLastSuccessfulLayout() {
    const { participants } = getCallState(yield select());

    // if last layout was null, we want 'everyone' to be displayed
    const targetLayout = lastSuccessfullLayout ?? Array.from(participants.keys());

    const updates = Array.from(participants.entries())
      .filter(([, p]) => p.isPresent && p.isVideoRecorded != null)
      .map(([visitorId, p]) =>
        targetLayout.includes(visitorId) === p.isVideoRecorded
          ? null
          : { visitorId, props: { isVideoRecorded: !p.isVideoRecorded } }
      )
      .filter((x) => x);

    // Sagas are typically an alternative to thunks. However, we use them together due to
    // historical reasons. Thunk actions do not have 'type' property but put() typing
    // requires it even though it clearly works with thunks without 'type'property. This
    // manual casting magic avoids TS errors.
    yield put(broadcastParticipantUpdate(updates) as unknown as Action);
  }

  function* updateLayout(storyId: string, videoParticipants: string[]) {
    try {
      yield call([axios, "post"], `/stories/${storyId}/update_recording`, { videoParticipants });
      lastSuccessfullLayout = videoParticipants;
    } catch (err) {
      Sentry.captureException(err);
      console.log(err);
      if (typeof lastSuccessfullLayout !== "undefined") {
        yield all([
          call(revertToLastSuccessfulLayout),
          put(
            showMessageBox({
              title: "Layout Update Error",
              message: "Encountered a server error while attempting to change video recording layout",
            }) as unknown as Action
          ),
        ]);
      }
    }

    updateInProgress = false;
  }

  while (true) {
    const { update, setParticipants, timeout } = yield race({
      update: take(CALL_SEND_PEER_INFO),
      setParticipants: take(CALL_SET_VIDEO_PARTICIPANTS),
      ...(pendingLayoutUpdate ? { timeout: delay(LAYOUT_UPDATE_DEBOUNCE) } : {}),
    });

    if (update && isLayoutUpdate(update)) {
      pendingLayoutUpdate = true;
    }
    if (setParticipants) {
      lastSuccessfullLayout = setParticipants.payload;
    }

    if (timeout && pendingLayoutUpdate && !updateInProgress) {
      const { storyId, recording, participants } = getCallState(yield select());
      if (recording) {
        const newLayout = getVideoParticipantIds(participants, true);
        if (!isEqual(newLayout, lastSuccessfullLayout)) {
          updateInProgress = true;
          yield fork(updateLayout, storyId, newLayout);
        }
      }
      pendingLayoutUpdate = false;
    }
  }
}

function* checkGuestAuthOnCallJoin() {
  while (true) {
    const data = yield take(initializeCall.type);
    if (initializeCall.match(data) && data.payload.guestToken) {
      yield put(
        authenticatedAction({
          token: data.payload.guestToken,
          role: "guest",
        }) as any
      );
    }
  }
}

export function* callSagas() {
  yield all([call(handleRecordingLayoutChanges), call(checkGuestAuthOnCallJoin)]);
}
